import { Link, Text, View } from '@react-pdf/renderer'
import type { CSSProperties, ReactNode } from 'react'

import type { ReduxQuote } from 'src/types/apiType'

type ProductionNotesField = keyof Pick<
	ReduxQuote,
	| 'introduction'
	| 'shooting'
	| 'prep'
	| 'shootingCountry'
	| 'prelight'
	| 'casting'
	| 'crew'
	| 'buyouts'
	| 'locations'
	| 'studio'
	| 'artDepartment'
	| 'wardrobe'
	| 'equipment'
	| 'travel'
	| 'accommodation'
	| 'editing'
	| 'imagePostproduction'
	| 'soundPostproduction'
	| 'deliverables'
	| 'insurance'
	| 'quoteGuidelines'
	| 'excluded'
	| 'paymentTerms'
	| 'miscellaneous'
	| 'conclusion'
	| 'description'
	| 'service'
	| 'scope'
	| 'vhr'
>

type SanitizedFragment = {
	html: string
	kind: 'inline' | 'block' | 'blank' | 'empty'
}

type ReactRenderContext = {
	paragraphClassName?: string
	paragraphStyle?: CSSProperties
	keyPrefix: string
}

type PdfRenderContext = {
	paragraphStyle: any
	keyPrefix: string
	paragraphSpacing: number
	listDepth: number
}

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i
const INLINE_FORMAT_TAGS = new Set(['strong', 'em', 'u', 's', 'a'])
const ALLOWED_BLOCK_TAGS = new Set([
	'p',
	'h1',
	'h2',
	'h3',
	'blockquote',
	'pre',
	'ul',
	'ol',
	'li',
])
const GENERIC_BLOCK_TAGS = new Set([
	'address',
	'article',
	'aside',
	'details',
	'div',
	'dl',
	'dt',
	'dd',
	'fieldset',
	'figcaption',
	'figure',
	'footer',
	'form',
	'h4',
	'h5',
	'h6',
	'header',
	'hr',
	'main',
	'nav',
	'section',
	'table',
	'tbody',
	'td',
	'tfoot',
	'th',
	'thead',
	'tr',
])
const SKIP_TAGS = new Set([
	'script',
	'style',
	'noscript',
	'iframe',
	'object',
	'embed',
	'svg',
	'math',
])

export const PRODUCTION_NOTES_FIELDS = [
	'introduction',
	'shooting',
	'prep',
	'shootingCountry',
	'prelight',
	'casting',
	'crew',
	'buyouts',
	'locations',
	'studio',
	'artDepartment',
	'wardrobe',
	'equipment',
	'travel',
	'accommodation',
	'editing',
	'imagePostproduction',
	'soundPostproduction',
	'deliverables',
	'insurance',
	'quoteGuidelines',
	'excluded',
	'paymentTerms',
	'miscellaneous',
	'conclusion',
] as const satisfies readonly ProductionNotesField[]

export const SOUND_PRODUCTION_NOTES_FIELDS = [
	'introduction',
	'shooting',
	'crew',
	'deliverables',
	'quoteGuidelines',
	'miscellaneous',
	'description',
	'service',
	'scope',
	'vhr',
] as const satisfies readonly ProductionNotesField[]

export const TEMPLATE_360_PRODUCTION_NOTES_FIELDS = [
	'introduction',
	'prep',
	'casting',
	'crew',
	'locations',
	'wardrobe',
	'editing',
	'imagePostproduction',
	'deliverables',
	'miscellaneous',
] as const satisfies readonly ProductionNotesField[]

export const ALL_PRODUCTION_NOTES_FIELDS = [
	...new Set([
		...PRODUCTION_NOTES_FIELDS,
		...SOUND_PRODUCTION_NOTES_FIELDS,
		...TEMPLATE_360_PRODUCTION_NOTES_FIELDS,
	]),
] as readonly ProductionNotesField[]

function getParser() {
	if (typeof DOMParser !== 'undefined') {
		return new DOMParser()
	}

	return null
}

function escapeHtml(value: string) {
	return value
		.replaceAll('&', '&amp;')
		.replaceAll('<', '&lt;')
		.replaceAll('>', '&gt;')
}

function escapeAttribute(value: string) {
	return escapeHtml(value).replaceAll('"', '&quot;')
}

function hasHtmlMarkup(value: string) {
	return HTML_TAG_PATTERN.test(value)
}

function normalizeNewlines(value: string) {
	return value.replace(/\r\n?/g, '\n')
}

function normalizePlainTextInput(value: string) {
	const escapedValue = escapeHtml(normalizeNewlines(value))
	const lines = escapedValue.split('\n')

	if (lines.length === 0) {
		return ''
	}

	return `<p>${lines.join('<br />')}</p>`
}

function getRenderableHtml(value: string) {
	if (!value) {
		return ''
	}

	if (!hasHtmlMarkup(value)) {
		return normalizePlainTextInput(value)
	}

	return value
}

function getParsedContainer(value: string) {
	const parser = getParser()
	if (!parser) {
		return null
	}

	const document = parser.parseFromString(value, 'text/html')
	return document.body
}

function isHtmlFragmentBlank(value: string) {
	return (
		value
			.replace(/<br\s*\/?>/gi, '')
			.replace(/<\/?(p|h1|h2|h3|blockquote|pre|ul|ol|li)>/gi, '')
			.replace(/&nbsp;|\u00A0/gi, ' ')
			.replace(/<[^>]+>/g, '')
			.trim().length === 0
	)
}

function isSanitizedHtmlBlank(value: string) {
	const container = getParsedContainer(value)
	if (!container) {
		return isHtmlFragmentBlank(value)
	}

	const textValue = (container.textContent ?? '')
		.replace(/\u00A0/g, ' ')
		.trim()

	return textValue.length === 0
}

function sanitizeHref(rawHref: string | null) {
	if (!rawHref) {
		return null
	}

	const trimmedHref = rawHref.trim()
	if (!trimmedHref) {
		return null
	}

	if (
		trimmedHref.startsWith('/') ||
		trimmedHref.startsWith('#') ||
		trimmedHref.startsWith('?')
	) {
		return trimmedHref
	}

	const protocolPattern = /^[a-zA-Z][a-zA-Z\d+\-.]*:/
	if (!protocolPattern.test(trimmedHref)) {
		return trimmedHref
	}

	const lowerHref = trimmedHref.toLowerCase()
	if (
		lowerHref.startsWith('http:') ||
		lowerHref.startsWith('https:') ||
		lowerHref.startsWith('mailto:') ||
		lowerHref.startsWith('tel:')
	) {
		return trimmedHref
	}

	return null
}

function buildParagraphFragment(html: string): SanitizedFragment {
	if (isHtmlFragmentBlank(html)) {
		return { html: '', kind: 'empty' }
	}

	return {
		html: `<p>${html}</p>`,
		kind: 'block',
	}
}

function buildBlankLineFragment(): SanitizedFragment {
	return { html: '<p><br /></p>', kind: 'blank' }
}

function isInlineBlankLineFragment(fragment: SanitizedFragment) {
	return (
		fragment.kind === 'inline' &&
		/<br\s*\/?>/i.test(fragment.html) &&
		isHtmlFragmentBlank(fragment.html)
	)
}

function hasBlankLineChildFragment(fragments: SanitizedFragment[]) {
	return fragments.some((fragment) => {
		return fragment.kind === 'blank' || isInlineBlankLineFragment(fragment)
	})
}

function hasRenderableFragment(fragments: SanitizedFragment[]) {
	return fragments.some((fragment) => {
		return (
			fragment.kind !== 'empty' &&
			fragment.kind !== 'blank' &&
			!isInlineBlankLineFragment(fragment)
		)
	})
}

function serializeFragments(
	fragments: SanitizedFragment[],
	wrapInlineInParagraphs: boolean
) {
	if (!wrapInlineInParagraphs) {
		return fragments
			.filter((fragment) => fragment.kind !== 'empty')
			.map((fragment) => fragment.html)
			.join('')
	}

	if (!hasRenderableFragment(fragments)) {
		return ''
	}

	let inlineHtml = ''
	let blankHtml = ''
	let result = ''

	for (const fragment of fragments) {
		if (fragment.kind === 'empty') {
			continue
		}

		if (fragment.kind === 'blank') {
			if (!isHtmlFragmentBlank(inlineHtml)) {
				result += `<p>${inlineHtml}</p>`
				inlineHtml = ''
			}
			blankHtml += fragment.html
			continue
		}

		if (fragment.kind === 'block') {
			if (!isHtmlFragmentBlank(inlineHtml)) {
				result += `<p>${inlineHtml}</p>`
			}
			inlineHtml = ''
			result += blankHtml
			blankHtml = ''
			result += fragment.html
			continue
		}

		if (blankHtml) {
			if (!isHtmlFragmentBlank(inlineHtml)) {
				result += `<p>${inlineHtml}</p>`
				inlineHtml = ''
			}
			result += blankHtml
			blankHtml = ''
		}
		inlineHtml += fragment.html
	}

	if (!isHtmlFragmentBlank(inlineHtml)) {
		result += `<p>${inlineHtml}</p>`
	}

	result += blankHtml

	return result
}

function sanitizeTextNodeFragment(
	node: ChildNode,
	preserveTextNewlines: boolean
): SanitizedFragment {
	const textValue = node.textContent ?? ''
	const escapedValue = preserveTextNewlines
		? escapeHtml(textValue)
		: normalizeNewlines(textValue)
				.split('\n')
				.map((line) => escapeHtml(line))
				.join('<br />')

	return escapedValue
		? { html: escapedValue, kind: 'inline' }
		: { html: '', kind: 'empty' }
}

function sanitizeNodeFragment(
	node: ChildNode,
	preserveTextNewlines = false
): SanitizedFragment {
	if (node.nodeType === Node.TEXT_NODE) {
		return sanitizeTextNodeFragment(node, preserveTextNewlines)
	}

	if (node.nodeType !== Node.ELEMENT_NODE) {
		return { html: '', kind: 'empty' }
	}

	const element = node as HTMLElement
	const originalTag = element.tagName.toLowerCase()
	if (SKIP_TAGS.has(originalTag)) {
		return { html: '', kind: 'empty' }
	}

	if (originalTag === 'br') {
		return { html: '<br />', kind: 'inline' }
	}

	const tag =
		originalTag === 'b'
			? 'strong'
			: originalTag === 'i'
				? 'em'
				: originalTag === 'strike'
					? 's'
					: originalTag

	const childFragments = Array.from(element.childNodes).map((child) =>
		sanitizeNodeFragment(child, tag === 'pre')
	)
	const childrenHtml = serializeFragments(childFragments, false)
	const hasBlockChildren = childFragments.some(
		(fragment) => fragment.kind === 'block'
	)

	if (INLINE_FORMAT_TAGS.has(tag)) {
		if (hasBlockChildren) {
			return buildParagraphFragment(
				serializeFragments(childFragments, true)
			)
		}

		if (!childrenHtml) {
			return { html: '', kind: 'empty' }
		}

		if (tag === 'a') {
			const href = sanitizeHref(element.getAttribute('href'))
			if (!href) {
				return { html: childrenHtml, kind: 'inline' }
			}

			return {
				html: `<a href="${escapeAttribute(href)}">${childrenHtml}</a>`,
				kind: 'inline',
			}
		}

		return {
			html: `<${tag}>${childrenHtml}</${tag}>`,
			kind: 'inline',
		}
	}

	if (tag === 'ul' || tag === 'ol') {
		const itemHtml = Array.from(element.childNodes)
			.map((child) => {
				if (
					child.nodeType === Node.ELEMENT_NODE &&
					(child as HTMLElement).tagName.toLowerCase() === 'li'
				) {
					return sanitizeNodeFragment(child).html
				}

				const fragment = sanitizeNodeFragment(child)
				if (
					fragment.kind === 'empty' ||
					isHtmlFragmentBlank(fragment.html)
				) {
					return ''
				}

				return `<li>${fragment.html}</li>`
			})
			.join('')

		if (!itemHtml) {
			return { html: '', kind: 'empty' }
		}

		return {
			html: `<${tag}>${itemHtml}</${tag}>`,
			kind: 'block',
		}
	}

	if (tag === 'li') {
		if (isHtmlFragmentBlank(childrenHtml)) {
			return { html: '', kind: 'empty' }
		}

		return {
			html: `<li>${childrenHtml}</li>`,
			kind: 'block',
		}
	}

	if (ALLOWED_BLOCK_TAGS.has(tag)) {
		if (isHtmlFragmentBlank(childrenHtml)) {
			if (hasBlankLineChildFragment(childFragments)) {
				return buildBlankLineFragment()
			}

			return { html: '', kind: 'empty' }
		}

		if (hasBlockChildren && tag !== 'blockquote') {
			return buildParagraphFragment(
				serializeFragments(childFragments, true)
			)
		}

		return {
			html: `<${tag}>${childrenHtml}</${tag}>`,
			kind: 'block',
		}
	}

	if (GENERIC_BLOCK_TAGS.has(tag)) {
		if (!childrenHtml || isHtmlFragmentBlank(childrenHtml)) {
			if (
				!hasRenderableFragment(childFragments) &&
				hasBlankLineChildFragment(childFragments)
			) {
				return buildBlankLineFragment()
			}

			return { html: '', kind: 'empty' }
		}

		if (hasBlockChildren) {
			return {
				html: serializeFragments(childFragments, true),
				kind: 'block',
			}
		}

		return buildParagraphFragment(childrenHtml)
	}

	if (hasBlockChildren) {
		return {
			html: serializeFragments(childFragments, true),
			kind: 'block',
		}
	}

	return childrenHtml
		? { html: childrenHtml, kind: 'inline' }
		: { html: '', kind: 'empty' }
}

export function sanitizeProductionNotesHtml(value: string) {
	if (!value) {
		return ''
	}

	const renderableValue = getRenderableHtml(value)
	const container = getParsedContainer(renderableValue)
	if (!container) {
		return value.trim()
	}

	const sanitizedValue = serializeFragments(
		Array.from(container.childNodes).map((child) =>
			sanitizeNodeFragment(child)
		),
		true
	).trim()

	if (!sanitizedValue || isSanitizedHtmlBlank(sanitizedValue)) {
		return ''
	}

	return sanitizedValue
}

function getNodePlainText(node: ChildNode): string {
	if (node.nodeType === Node.TEXT_NODE) {
		return node.textContent ?? ''
	}

	if (node.nodeType !== Node.ELEMENT_NODE) {
		return ''
	}

	const element = node as HTMLElement
	const tag = element.tagName.toLowerCase()

	if (SKIP_TAGS.has(tag)) {
		return ''
	}

	if (tag === 'br') {
		return '\n'
	}

	if (tag === 'ul' || tag === 'ol') {
		const listText = Array.from(element.children)
			.filter((child) => child.tagName.toLowerCase() === 'li')
			.map((child) => getNodePlainText(child).trim())
			.filter(Boolean)
			.join('\n')

		return listText ? `${listText}\n\n` : ''
	}

	const childrenText = Array.from(element.childNodes)
		.map((child) => getNodePlainText(child))
		.join('')

	if (tag === 'li') {
		return `${childrenText}\n`
	}

	if (
		tag === 'p' ||
		tag === 'h1' ||
		tag === 'h2' ||
		tag === 'h3' ||
		tag === 'blockquote' ||
		tag === 'pre'
	) {
		return `${childrenText}\n\n`
	}

	return childrenText
}

export function getProductionNotesPlainText(value: string) {
	if (!value) {
		return ''
	}

	const sanitizedValue = sanitizeProductionNotesHtml(value)
	if (!sanitizedValue) {
		return ''
	}

	const container = getParsedContainer(sanitizedValue)
	if (!container) {
		return sanitizedValue
	}

	return Array.from(container.childNodes)
		.map((child) => getNodePlainText(child))
		.join('')
		.replace(/\u00A0/g, ' ')
		.replace(/\n{3,}/g, '\n\n')
		.replace(/[ \t]+\n/g, '\n')
		.replace(/\n[ \t]+/g, '\n')
		.trim()
}

export function isProductionNotesHtmlBlank(value: string) {
	if (!value) {
		return true
	}

	const sanitizedValue = sanitizeProductionNotesHtml(value)
	if (!sanitizedValue) {
		return true
	}

	return isSanitizedHtmlBlank(sanitizedValue)
}

export function getProductionNotesVisibleLength(value: string) {
	return getProductionNotesPlainText(value).length
}

export function normalizeProductionNotesHtml(value: string) {
	return sanitizeProductionNotesHtml(getRenderableHtml(value))
}

export function normalizeProductionNotesForComparison(value: string) {
	return normalizeProductionNotesHtml(value)
}

export function sanitizeQuoteProductionNotes<T extends Partial<ReduxQuote>>(
	quote: T
) {
	const sanitizedQuote = { ...quote }

	for (const field of ALL_PRODUCTION_NOTES_FIELDS) {
		const currentValue = sanitizedQuote[field] as unknown
		if (typeof currentValue === 'string') {
			;(sanitizedQuote as Record<string, unknown>)[field] =
				sanitizeProductionNotesHtml(currentValue)
		}
	}

	if ('productionNoteLabels' in sanitizedQuote) {
		;(sanitizedQuote as Record<string, unknown>).productionNoteLabels =
			sanitizeProductionNoteLabels(
				(sanitizedQuote as Record<string, unknown>).productionNoteLabels
			)
	}

	return sanitizedQuote
}

/**
 * Production-note labels are stored on the quote as an AWSJSON map of
 * { productionNoteFieldName: labelHtml }. The value edited in the form uses the
 * same rich-text editor as the note bodies, so labels are sanitized/rendered
 * with the exact same helpers. Blank or default-equal entries are dropped so
 * that untouched labels always fall back to the default i18n string.
 */
export function parseProductionNoteLabels(
	raw: unknown
): Record<string, string> {
	if (!raw) {
		return {}
	}

	let parsed: unknown = raw
	if (typeof raw === 'string') {
		try {
			parsed = JSON.parse(raw)
		} catch {
			return {}
		}
	}

	if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
		return {}
	}

	const result: Record<string, string> = {}
	for (const [key, value] of Object.entries(parsed as object)) {
		if (typeof value === 'string') {
			result[key] = value
		}
	}

	return result
}

/**
 * Wraps a plain-text default label (from i18n) as sanitized HTML so it can seed
 * the rich-text editor and be compared against user overrides.
 */
export function defaultLabelToHtml(text: string | undefined | null): string {
	if (!text) {
		return ''
	}

	return getRenderableHtml(text)
}

/**
 * Sanitizes every label in a production-note label map, dropping blank entries.
 * Accepts a JSON string or object and returns a JSON string (or null when the
 * resulting map is empty) suitable for persisting to the AWSJSON quote field.
 */
export function sanitizeProductionNoteLabels(raw: unknown): string | null {
	const parsed = parseProductionNoteLabels(raw)
	const sanitized: Record<string, string> = {}

	for (const [field, value] of Object.entries(parsed)) {
		const sanitizedValue = sanitizeProductionNotesHtml(value)
		if (sanitizedValue && !isProductionNotesHtmlBlank(sanitizedValue)) {
			sanitized[field] = sanitizedValue
		}
	}

	if (Object.keys(sanitized).length === 0) {
		return null
	}

	return JSON.stringify(sanitized)
}

/**
 * Merges a single label override into an existing label map and returns the
 * AWSJSON string (or null) ready to persist. An empty `labelHtml` clears the
 * override for that field so it falls back to the default.
 */
export function mergeProductionNoteLabel(
	labels: Record<string, string>,
	field: string,
	labelHtml: string
): string | null {
	return sanitizeProductionNoteLabels({ ...labels, [field]: labelHtml })
}

function getReactBlockStyle(
	tag: string,
	paragraphStyle?: CSSProperties
): CSSProperties {
	const baseStyle: CSSProperties = {
		margin: 0,
		whiteSpace: 'break-spaces',
		...paragraphStyle,
	}

	if (tag === 'ul' || tag === 'ol') {
		return {
			...baseStyle,
			paddingInlineStart: '1.5rem',
			marginTop: '0.5rem',
			marginBottom: '0.5rem',
		}
	}

	if (tag === 'li') {
		return {
			...baseStyle,
			marginBottom: '0.25rem',
		}
	}

	if (tag === 'h1') {
		return {
			...baseStyle,
			fontSize: '1.5em',
			fontWeight: 700,
		}
	}

	if (tag === 'h2') {
		return {
			...baseStyle,
			fontSize: '1.25em',
			fontWeight: 700,
		}
	}

	if (tag === 'h3') {
		return {
			...baseStyle,
			fontSize: '1.1em',
			fontWeight: 700,
		}
	}

	if (tag === 'blockquote') {
		return {
			...baseStyle,
			borderLeft: '3px solid rgba(0, 0, 0, 0.2)',
			paddingLeft: '0.75rem',
			fontStyle: 'italic',
		}
	}

	if (tag === 'pre') {
		return {
			...baseStyle,
			fontFamily: 'monospace',
			whiteSpace: 'pre-wrap',
		}
	}

	return baseStyle
}

function renderProductionNotesNodeToReact(
	node: ChildNode,
	context: ReactRenderContext,
	key: string
): ReactNode {
	if (node.nodeType === Node.TEXT_NODE) {
		return node.textContent
	}

	if (node.nodeType !== Node.ELEMENT_NODE) {
		return null
	}

	const element = node as HTMLElement
	const tag = element.tagName.toLowerCase()
	const children = Array.from(element.childNodes).map((child, index) =>
		renderProductionNotesNodeToReact(child, context, `${key}-${index}`)
	)

	if (tag === 'br') {
		return <br key={key} />
	}

	if (tag === 'strong') {
		return <strong key={key}>{children}</strong>
	}

	if (tag === 'em') {
		return <em key={key}>{children}</em>
	}

	if (tag === 'u') {
		return <u key={key}>{children}</u>
	}

	if (tag === 's') {
		return <s key={key}>{children}</s>
	}

	if (tag === 'a') {
		const href = sanitizeHref(element.getAttribute('href'))
		if (!href) {
			return <span key={key}>{children}</span>
		}

		return (
			<a key={key} href={href} target="_blank" rel="noreferrer">
				{children}
			</a>
		)
	}

	if (tag === 'ul' || tag === 'ol') {
		const TagName = tag
		return (
			<TagName
				key={key}
				className={context.paragraphClassName}
				style={getReactBlockStyle(tag, context.paragraphStyle)}
			>
				{children}
			</TagName>
		)
	}

	if (
		tag === 'p' ||
		tag === 'h1' ||
		tag === 'h2' ||
		tag === 'h3' ||
		tag === 'blockquote' ||
		tag === 'pre' ||
		tag === 'li'
	) {
		const TagName = tag
		return (
			<TagName
				key={key}
				className={context.paragraphClassName}
				style={getReactBlockStyle(tag, context.paragraphStyle)}
			>
				{children}
			</TagName>
		)
	}

	return <span key={key}>{children}</span>
}

function preserveSpacesForPdf(value: string) {
	return value.replace(/(^ +)|( +$)| {2,}/gm, (match) => {
		if (!match.includes(' ')) {
			return match
		}

		return match
			.split('')
			.map((char, index) => (char === ' ' && index > 0 ? '\u00A0' : char))
			.join('')
	})
}

function getPdfTextDecoration(
	currentDecoration: string | undefined,
	nextDecoration: string
) {
	if (!currentDecoration) {
		return nextDecoration
	}

	if (currentDecoration.includes(nextDecoration)) {
		return currentDecoration
	}

	return `${currentDecoration} ${nextDecoration}`
}

function getPdfInlineStyle(tag: string, href?: string | null): any {
	const inlineStyle: any = {}

	if (tag === 'strong') {
		inlineStyle.fontWeight = 'bold'
	}

	if (tag === 'em') {
		inlineStyle.fontStyle = 'italic'
	}

	if (tag === 'u') {
		inlineStyle.textDecoration = getPdfTextDecoration(
			inlineStyle.textDecoration as string | undefined,
			'underline'
		)
	}

	if (tag === 's') {
		inlineStyle.textDecoration = getPdfTextDecoration(
			inlineStyle.textDecoration as string | undefined,
			'line-through'
		)
	}

	if (tag === 'a' && href) {
		inlineStyle.color = '#1a5fb4'
		inlineStyle.textDecoration = getPdfTextDecoration(
			inlineStyle.textDecoration as string | undefined,
			'underline'
		)
	}

	return inlineStyle
}

function renderPdfInlineNodes(
	nodes: ChildNode[],
	keyPrefix: string
): ReactNode[] {
	return nodes.map((node, index) => {
		const key = `${keyPrefix}-${index}`

		if (node.nodeType === Node.TEXT_NODE) {
			return preserveSpacesForPdf(node.textContent ?? '')
		}

		if (node.nodeType !== Node.ELEMENT_NODE) {
			return null
		}

		const element = node as HTMLElement
		const tag = element.tagName.toLowerCase()

		if (tag === 'br') {
			return '\n'
		}

		if (tag === 'ul' || tag === 'ol') {
			return '\n'
		}

		const childNodes = Array.from(element.childNodes)
		const children = renderPdfInlineNodes(childNodes, key)

		if (tag === 'strong' || tag === 'em' || tag === 'u' || tag === 's') {
			return (
				<Text key={key} style={getPdfInlineStyle(tag)}>
					{children}
				</Text>
			)
		}

		if (tag === 'a') {
			const href = sanitizeHref(element.getAttribute('href'))
			if (!href) {
				return children
			}

			return (
				<Link key={key} src={href} style={getPdfInlineStyle(tag, href)}>
					{children}
				</Link>
			)
		}

		return <Text key={key}>{children}</Text>
	})
}

function getPdfBlockTextStyle(tag: string, paragraphStyle: any) {
	const baseStyle = { ...paragraphStyle }

	if (tag === 'h1') {
		return {
			...baseStyle,
			fontSize: Math.max((baseStyle.fontSize ?? 10) + 4, 12),
			fontWeight: 'bold',
		}
	}

	if (tag === 'h2') {
		return {
			...baseStyle,
			fontSize: Math.max((baseStyle.fontSize ?? 10) + 2, 11),
			fontWeight: 'bold',
		}
	}

	if (tag === 'h3') {
		return {
			...baseStyle,
			fontSize: Math.max((baseStyle.fontSize ?? 10) + 1, 10),
			fontWeight: 'bold',
		}
	}

	if (tag === 'pre') {
		return {
			...baseStyle,
			fontFamily: 'Courier',
		}
	}

	if (tag === 'blockquote') {
		return {
			...baseStyle,
			fontStyle: 'italic',
		}
	}

	return baseStyle
}

function isBlankLineElement(element: HTMLElement) {
	return (
		(element.textContent ?? '').replace(/\u00A0/g, ' ').trim() === '' &&
		Array.from(element.childNodes).some((child) => {
			return (
				child.nodeType === Node.ELEMENT_NODE &&
				(child as HTMLElement).tagName.toLowerCase() === 'br'
			)
		})
	)
}

function hasInlineBreakElement(nodes: ChildNode[]) {
	return nodes.some((node) => {
		return (
			node.nodeType === Node.ELEMENT_NODE &&
			(node as HTMLElement).tagName.toLowerCase() === 'br'
		)
	})
}

function splitInlineNodesOnBreaks(nodes: ChildNode[]) {
	const lines: ChildNode[][] = [[]]

	for (const node of nodes) {
		if (
			node.nodeType === Node.ELEMENT_NODE &&
			(node as HTMLElement).tagName.toLowerCase() === 'br'
		) {
			lines.push([])
			continue
		}

		lines[lines.length - 1].push(node)
	}

	return lines
}

function renderPdfInlineLines(
	nodes: ChildNode[],
	context: PdfRenderContext,
	key: string
) {
	return (
		<View key={key}>
			{splitInlineNodesOnBreaks(nodes).map((lineNodes, lineIndex) => {
				const isBlankLine =
					lineNodes.length === 0 ||
					lineNodes.every((lineNode) => {
						return (
							(lineNode.textContent ?? '')
								.replace(/\u00A0/g, ' ')
								.trim() === ''
						)
					})

				return (
					<Text
						key={`${key}-line-${lineIndex}`}
						style={context.paragraphStyle}
					>
						{isBlankLine
							? '\u00A0'
							: renderPdfInlineNodes(
									lineNodes,
									`${key}-line-${lineIndex}`
								)}
					</Text>
				)
			})}
		</View>
	)
}

function renderPdfListItem(
	node: HTMLElement,
	index: number,
	isOrdered: boolean,
	context: PdfRenderContext,
	key: string
) {
	const nestedLists = Array.from(node.children).filter((child) => {
		const tag = child.tagName.toLowerCase()
		return tag === 'ul' || tag === 'ol'
	})
	const inlineNodes = Array.from(node.childNodes).filter((child) => {
		return !(
			child.nodeType === Node.ELEMENT_NODE &&
			['ul', 'ol'].includes((child as HTMLElement).tagName.toLowerCase())
		)
	})

	return (
		<View
			key={key}
			style={{
				marginTop: index === 0 ? 0 : 2,
			}}
		>
			<View style={{ flexDirection: 'row' }}>
				<Text
					style={{
						...context.paragraphStyle,
						marginRight: 4,
					}}
				>
					{isOrdered ? `${index + 1}.` : '\u2022'}
				</Text>
				<Text
					style={{
						...context.paragraphStyle,
						flexGrow: 1,
						flexShrink: 1,
					}}
				>
					{renderPdfInlineNodes(inlineNodes, `${key}-inline`)}
				</Text>
			</View>
			{nestedLists.length > 0 ? (
				<View style={{ paddingLeft: 12, marginTop: 2 }}>
					{nestedLists.map((child, childIndex) =>
						renderProductionNotesNodeToPdf(
							child as HTMLElement,
							{
								...context,
								listDepth: context.listDepth + 1,
							},
							`${key}-nested-${childIndex}`,
							childIndex === 0 ? 0 : 2
						)
					)}
				</View>
			) : null}
		</View>
	)
}

function renderProductionNotesNodeToPdf(
	node: ChildNode,
	context: PdfRenderContext,
	key: string,
	marginTop: number
): ReactNode {
	if (node.nodeType === Node.TEXT_NODE) {
		const textValue = preserveSpacesForPdf(node.textContent ?? '')
		if (!textValue.trim()) {
			return null
		}

		return (
			<Text
				key={key}
				style={{
					...context.paragraphStyle,
					marginTop,
				}}
			>
				{textValue}
			</Text>
		)
	}

	if (node.nodeType !== Node.ELEMENT_NODE) {
		return null
	}

	const element = node as HTMLElement
	const tag = element.tagName.toLowerCase()
	const childNodes = Array.from(element.childNodes)

	if (tag === 'p' && isBlankLineElement(element)) {
		return (
			<Text
				key={key}
				style={{
					...context.paragraphStyle,
					marginTop,
				}}
			>
				{'\u00A0'}
			</Text>
		)
	}

	if (tag === 'ul' || tag === 'ol') {
		return (
			<View
				key={key}
				style={{
					marginTop,
					paddingLeft: context.listDepth > 0 ? 4 : 0,
				}}
			>
				{Array.from(element.children)
					.filter((child) => child.tagName.toLowerCase() === 'li')
					.map((child, index) =>
						renderPdfListItem(
							child as HTMLElement,
							index,
							tag === 'ol',
							context,
							`${key}-${index}`
						)
					)}
			</View>
		)
	}

	if (tag === 'blockquote') {
		return (
			<View
				key={key}
				style={{
					marginTop,
					paddingLeft: 8,
					borderLeftWidth: 2,
					borderLeftColor: '#B0B7C3',
				}}
			>
				<Text style={getPdfBlockTextStyle(tag, context.paragraphStyle)}>
					{renderPdfInlineNodes(childNodes, key)}
				</Text>
			</View>
		)
	}

	if (
		tag === 'p' ||
		tag === 'h1' ||
		tag === 'h2' ||
		tag === 'h3' ||
		tag === 'pre'
	) {
		if (tag === 'p' && hasInlineBreakElement(childNodes)) {
			return (
				<View
					key={key}
					style={{
						marginTop,
					}}
				>
					{renderPdfInlineLines(childNodes, context, key)}
				</View>
			)
		}

		return (
			<Text
				key={key}
				style={{
					...getPdfBlockTextStyle(tag, context.paragraphStyle),
					marginTop,
				}}
			>
				{renderPdfInlineNodes(childNodes, key)}
			</Text>
		)
	}

	return (
		<Text
			key={key}
			style={{
				...context.paragraphStyle,
				marginTop,
			}}
		>
			{renderPdfInlineNodes(childNodes, key)}
		</Text>
	)
}

export function renderProductionNotesHtmlToReact({
	html,
	paragraphClassName,
	paragraphStyle,
	keyPrefix = 'production-notes',
}: {
	html: string
	paragraphClassName?: string
	paragraphStyle?: CSSProperties
	keyPrefix?: string
}) {
	const sanitizedValue = normalizeProductionNotesHtml(html)
	if (!sanitizedValue) {
		return null
	}

	const container = getParsedContainer(sanitizedValue)
	if (!container) {
		return null
	}

	return Array.from(container.childNodes).map((node, index) =>
		renderProductionNotesNodeToReact(
			node,
			{
				paragraphClassName,
				paragraphStyle,
				keyPrefix,
			},
			`${keyPrefix}-${index}`
		)
	)
}

export function renderProductionNotesHtmlToPdf({
	html,
	paragraphStyle,
	keyPrefix = 'production-notes',
	paragraphSpacing = 4,
}: {
	html: string
	paragraphStyle: any
	keyPrefix?: string
	paragraphSpacing?: number
}) {
	const sanitizedValue = normalizeProductionNotesHtml(html)
	if (!sanitizedValue) {
		return null
	}

	const container = getParsedContainer(sanitizedValue)
	if (!container) {
		return null
	}

	return Array.from(container.childNodes).map((node, index) =>
		renderProductionNotesNodeToPdf(
			node,
			{
				paragraphStyle,
				keyPrefix,
				paragraphSpacing,
				listDepth: 0,
			},
			`${keyPrefix}-${index}`,
			index === 0 ? 0 : paragraphSpacing
		)
	)
}

// Must match the locale folders under src/i18n — the label editor compares
// overrides against the default in the language the user edited in, which is
// not stored, so "did the text change" checks every supported language.
const PRODUCTION_NOTE_LABEL_LANGUAGES = ['en', 'fr'] as const

function normalizeLabelPlainText(value: string) {
	return value.replace(/\s+/g, ' ').trim()
}

/**
 * Quote PDFs and the public bid page historically render the introduction
 * section without a heading, so its label override is only shown when the
 * user actually renamed it: the override's visible text must differ from the
 * default introduction label in every supported language. Formatting-only
 * edits (e.g. underlining the default text) keep the heading hidden.
 */
export function hasCustomIntroductionLabelText(
	labels: Record<string, string>,
	i18nInstance: {
		getFixedT: (
			language: string,
			namespace: string
		) => (key: string) => string
	}
): boolean {
	const override = labels.introduction
	if (!override || isProductionNotesHtmlBlank(override)) {
		return false
	}

	const overrideText = normalizeLabelPlainText(
		getProductionNotesPlainText(override)
	)
	if (!overrideText) {
		return false
	}

	return PRODUCTION_NOTE_LABEL_LANGUAGES.every((language) => {
		const defaultText = i18nInstance.getFixedT(
			language,
			'projectChildren'
		)('addQuoteModal.sections.sectionThree.introduction')
		return normalizeLabelPlainText(defaultText) !== overrideText
	})
}

/**
 * Renders a production-note section label for the PDF. When the quote has a
 * (non-blank) label override, its rich-text HTML is rendered with the label
 * style; otherwise the plain default label is rendered as `<Text>` with the
 * trailing ": " separator used elsewhere in the PDFs.
 */
export function renderProductionNoteLabelToPdf({
	field,
	labels,
	defaultLabelText,
	labelStyle,
	keyPrefix = 'production-note-label',
}: {
	field: string
	labels: Record<string, string>
	defaultLabelText: string
	labelStyle: any
	keyPrefix?: string
}): ReactNode {
	const override = labels[field]
	if (override && !isProductionNotesHtmlBlank(override)) {
		return renderProductionNotesHtmlToPdf({
			html: override,
			paragraphStyle: labelStyle,
			keyPrefix: `${keyPrefix}-${field}`,
		})
	}

	return <Text style={labelStyle}>{`${defaultLabelText}: `}</Text>
}

/**
 * Renders a production-note section label for the on-screen React quote view,
 * mirroring {@link renderProductionNoteLabelToPdf}. Returns rendered HTML for a
 * label override, otherwise the plain default label text with a ": " suffix.
 */
export function renderProductionNoteLabelToReact({
	field,
	labels,
	defaultLabelText,
	paragraphClassName,
	paragraphStyle,
	keyPrefix = 'production-note-label',
}: {
	field: string
	labels: Record<string, string>
	defaultLabelText: string
	paragraphClassName?: string
	paragraphStyle?: CSSProperties
	keyPrefix?: string
}): ReactNode {
	const override = labels[field]
	if (override && !isProductionNotesHtmlBlank(override)) {
		return renderProductionNotesHtmlToReact({
			html: override,
			paragraphClassName,
			paragraphStyle,
			keyPrefix: `${keyPrefix}-${field}`,
		})
	}

	return `${defaultLabelText}: `
}
